import { createContext, useCallback, useContext, useRef, useState } from "react";
import {
    AlertTriangle, Check, CheckCircle2, FileSpreadsheet, Loader2,
    MinusCircle, Upload, UploadCloud, X,
} from "lucide-react";

import { Button } from "../ui/button";
import { Reactable } from "../ui/reactable";
import { cn } from "../../utils/tailwindUtils";
import ToastMsg from "../common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { BIOMETRIC_EMPLOYEE_MAPPING } from "../../constants/api";

/**
 * Upload a CSV mapping employees to their code on the reader.
 *
 * Preview first, always. The file comes from whoever installed the hardware
 * and routinely disagrees with the employee register, so the useful question
 * is "what will this do" rather than "did it work". Nothing is written until
 * the verdicts have been seen and Apply is pressed.
 */

const STATUS_META = {
    ok: {
        label: "Will map", Icon: CheckCircle2,
        className: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    },
    skipped: {
        label: "Skipped", Icon: MinusCircle,
        className: "bg-gray-100 text-gray-600 dark:bg-gray-700/40 dark:text-gray-300",
    },
    error: {
        label: "Problem", Icon: AlertTriangle,
        className: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    },
};

const REQUIRED_COLUMNS = [
    { key: "employee_code", hint: "Code on the reader" },
    { key: "hrzyra_employee_code", hint: "HRZyra employee code" },
];

const STEPS = [
    { id: 1, label: "File" },
    { id: 2, label: "Preview" },
    { id: 3, label: "Apply" },
];

const csvCell = (value) => {
    const text = value == null ? "" : String(value);
    if (/[",\n\r]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
    return text;
};

// Apply and a later preview must send what is on screen, including a code
// typed into the table, not the file that was first chosen.
const csvFromRows = (rows) => {
    const lines = ["employee_code,hrzyra_employee_code"];
    rows.forEach((r) => {
        lines.push([csvCell(r.employee_code), csvCell(r.hrzyra_employee_code)].join(","));
    });
    return new File([lines.join("\n")], "mapping.csv", { type: "text/csv" });
};

const isCsvFile = (f) => {
    if (!f) return false;
    const name = (f.name || "").toLowerCase();
    const type = (f.type || "").toLowerCase();
    return name.endsWith(".csv") || type === "text/csv" || type === "application/vnd.ms-excel";
};

const formatFileSize = (bytes) => {
    if (bytes == null || Number.isNaN(bytes)) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const PreviewEditContext = createContext(null);

const PreviewCodeCell = ({ row }) => (
    <span className="font-mono text-xs">{row.employee_code || "—"}</span>
);

const PreviewHrzyraCodeCell = ({ row }) => {
    const { updateCode, busy } = useContext(PreviewEditContext);
    return (
        <input
            type="text"
            value={row.hrzyra_employee_code || ""}
            onChange={(e) => updateCode(row.row, e.target.value)}
            disabled={!!busy}
            placeholder="Employee code"
            className="w-full rounded-md border border-input bg-background px-2 py-1 font-mono text-sm"
        />
    );
};

const PreviewEmployeeCell = ({ row }) => row.employee_name || "—";

const PreviewStatusCell = ({ row }) => {
    const meta = STATUS_META[row.status] || STATUS_META.error;
    const { Icon } = meta;
    return (
        <span className={cn(
            "inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full",
            meta.className,
        )}>
            <Icon className="w-3 h-3" /> {meta.label}
        </span>
    );
};

const PreviewReasonCell = ({ row }) => (
    <span className="text-xs text-muted-foreground">{row.reason || ""}</span>
);

const PREVIEW_COLUMNS = [
    { accessor: "row", Header: "Row", sortable: true, width: "70px" },
    {
        accessor: "employee_code", Header: "Device code", sortable: true, filterable: true,
        Cell: PreviewCodeCell,
    },
    {
        accessor: "hrzyra_employee_code", Header: "HRZyra code", sortable: true, filterable: true,
        width: "180px",
        Cell: PreviewHrzyraCodeCell,
    },
    {
        accessor: "employee_name", Header: "Employee", sortable: true, filterable: true,
        Cell: PreviewEmployeeCell,
    },
    {
        accessor: "status", Header: "Status", sortable: true, filterable: true, filterType: "select",
        Cell: PreviewStatusCell,
    },
    {
        accessor: "reason", Header: "Reason", sortable: true,
        Cell: PreviewReasonCell,
    },
];

const MappingUploadPanel = ({ locationId, onApplied }) => {
    const { create } = useApiMethods();
    const fileInput = useRef(null);

    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(null);
    const [dragCount, setDragCount] = useState(0);

    const acceptFile = useCallback((next) => {
        if (!next) return;
        if (!isCsvFile(next)) {
            ToastMsg("error", "Choose a CSV file");
            return;
        }
        setFile(next);
        setPreview(null);
        if (fileInput.current) fileInput.current.value = "";
    }, []);

    const clearFile = useCallback(() => {
        setFile(null);
        setPreview(null);
        setDragCount(0);
        if (fileInput.current) fileInput.current.value = "";
    }, []);

    const updateCode = useCallback((rowNo, value) => {
        setPreview((prev) => {
            if (!prev?.rows) return prev;
            return {
                ...prev,
                rows: prev.rows.map((r) => (
                    r.row === rowNo ? { ...r, hrzyra_employee_code: value } : r
                )),
            };
        });
    }, []);

    const send = async (isPreview) => {
        const source = preview?.rows?.length ? csvFromRows(preview.rows) : file;
        if (!source) return ToastMsg("error", "Choose a CSV file first");
        if (locationId === null || locationId === undefined || locationId === "") {
            return ToastMsg("error", "No device location is available");
        }

        const form = new FormData();
        form.append("file", source);
        const query = `?locations=${encodeURIComponent(locationId)}` +
            (isPreview ? "&preview=true" : "");

        setBusy(isPreview ? "preview" : "apply");
        try {
            const res = await create(`${BIOMETRIC_EMPLOYEE_MAPPING}${query}`, form, true);
            // A refused upload still carries the per-row verdicts, and those
            // are the whole point — show them rather than only the message.
            if (res?.data?.rows) setPreview(res.data);
            if (res?.status === "success") {
                ToastMsg("success", res.message);
                if (!isPreview) {
                    setFile(null);
                    if (fileInput.current) fileInput.current.value = "";
                    setPreview(null);
                    onApplied?.();
                }
            } else {
                ToastMsg("error", res?.message || "Upload failed");
            }
        } catch (_) {
            ToastMsg("error", "Upload failed");
        } finally {
            setBusy(null);
        }
    };

    const summary = preview?.summary;
    const rows = (preview?.rows || []).map((r) => ({ ...r, id: r.row }));
    const readyCount = rows.filter((r) =>
        String(r.employee_code || "").trim() && String(r.hrzyra_employee_code || "").trim()
    ).length;
    const dragging = dragCount > 0;
    const activeStep = !file ? 1 : !summary ? 2 : 3;
    const isBusy = !!busy;
    const canApply = !isBusy && readyCount > 0;

    let hint = "Choose a CSV to begin. Nothing is saved until you apply.";
    if (file && !summary) {
        hint = "Preview the file to see which rows will map before anything is saved.";
    } else if (summary && readyCount === 0) {
        hint = "Type an HRZyra employee code on the rows you want to map, then preview again.";
    } else if (summary && readyCount > 0 && !summary.ok) {
        hint = `${readyCount} code${readyCount === 1 ? "" : "s"} entered. Preview checks them; Apply saves the ones that match.`;
    } else if (summary) {
        hint = `${summary.ok} of ${summary.total} row${summary.total === 1 ? "" : "s"} will be mapped. Apply writes only those matches.`;
    }

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-border overflow-hidden">
            <div className="p-4 sm:p-5 space-y-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex items-start gap-3 min-w-0">
                        <div className="h-10 w-10 flex-shrink-0 rounded-xl bg-sidebar-primary/10 text-sidebar-primary grid place-items-center">
                            <UploadCloud className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                                Map employees from a file
                            </h3>
                            <p className="text-xs text-muted-foreground mt-0.5">
                                Match reader codes to HRZyra employees. Preview first — nothing is saved until you apply.
                            </p>
                        </div>
                    </div>
                    <nav aria-label="Mapping steps" className="flex items-center gap-1.5 sm:pt-1 shrink-0">
                        {STEPS.map((step, i) => {
                            const done = activeStep > step.id;
                            const current = activeStep === step.id;
                            return (
                                <div key={step.id} className="flex items-center gap-1.5">
                                    {i > 0 && (
                                        <span className={cn(
                                            "h-px w-4 sm:w-6",
                                            done || current ? "bg-sidebar-primary/50" : "bg-border",
                                        )} />
                                    )}
                                    <span className={cn(
                                        "h-5 w-5 rounded-full text-[10px] font-semibold grid place-items-center",
                                        done && "bg-emerald-500 text-white",
                                        current && "bg-sidebar-primary text-white",
                                        !done && !current && "bg-muted text-muted-foreground",
                                    )}>
                                        {done ? <Check className="h-3 w-3" /> : step.id}
                                    </span>
                                    <span className={cn(
                                        "text-[11px] font-medium",
                                        current ? "text-foreground" : "text-muted-foreground",
                                    )}>
                                        {step.label}
                                    </span>
                                </div>
                            );
                        })}
                    </nav>
                </div>

                <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-[11px] text-muted-foreground mr-1">Required columns</span>
                    {REQUIRED_COLUMNS.map((col) => (
                        <span
                            key={col.key}
                            title={col.hint}
                            className="inline-flex items-center rounded-md border border-border bg-muted/60 px-1.5 py-0.5 font-mono text-[11px] text-foreground"
                        >
                            {col.key}
                        </span>
                    ))}
                </div>

                <input
                    ref={fileInput}
                    type="file"
                    accept=".csv,text/csv"
                    className="sr-only"
                    onChange={(e) => acceptFile(e.target.files?.[0])}
                />

                {!file ? (
                    <div
                        role="button"
                        tabIndex={0}
                        aria-label="Choose a CSV file"
                            onClick={() => !isBusy && fileInput.current?.click()}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                if (!isBusy) fileInput.current?.click();
                            }
                        }}
                        onDragEnter={(e) => { e.preventDefault(); setDragCount((c) => c + 1); }}
                        onDragOver={(e) => { e.preventDefault(); }}
                        onDragLeave={() => setDragCount((c) => Math.max(0, c - 1))}
                        onDrop={(e) => {
                            e.preventDefault();
                            setDragCount(0);
                            acceptFile(e.dataTransfer?.files?.[0]);
                        }}
                        className={cn(
                            "rounded-lg border-2 border-dashed px-4 py-5 text-center transition-colors cursor-pointer",
                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-primary/40",
                            dragging
                                ? "border-sidebar-primary bg-sidebar-primary/5"
                                : "border-border hover:border-sidebar-primary/50 hover:bg-muted/40",
                            isBusy && "pointer-events-none opacity-60",
                        )}
                    >
                        <UploadCloud className={cn(
                            "h-8 w-8 mx-auto mb-2",
                            dragging ? "text-sidebar-primary" : "text-muted-foreground",
                        )} />
                        <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                            {dragging ? "Drop the CSV to attach it" : "Drop a CSV here, or click to browse"}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                            One row per employee. Device code in the first column, HRZyra code in the second.
                        </p>
                    </div>
                ) : (
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2.5">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                            <div className="h-9 w-9 rounded-lg bg-sidebar-primary/10 text-sidebar-primary grid place-items-center shrink-0">
                                <FileSpreadsheet className="h-4 w-4" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-sm font-medium truncate text-gray-900 dark:text-gray-100">
                                    {file.name}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                    {formatFileSize(file.size)}
                                    {summary
                                        ? ` · ${summary.total} row${summary.total === 1 ? "" : "s"} previewed`
                                        : " · Ready to preview"}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={isBusy}
                                onClick={() => fileInput.current?.click()}
                            >
                                Change
                            </Button>
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                disabled={isBusy}
                                onClick={clearFile}
                                title="Remove file"
                                aria-label="Remove file"
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                )}

                {summary && (
                    <div className="grid grid-cols-3 gap-2">
                        {[
                            { label: "Will map", value: summary.ok ?? 0, className: "text-emerald-700 dark:text-emerald-300" },
                            { label: "Skipped", value: summary.skipped ?? 0, className: "text-muted-foreground" },
                            { label: "Problems", value: summary.errors ?? 0, className: "text-amber-700 dark:text-amber-300" },
                        ].map((stat) => (
                            <div
                                key={stat.label}
                                className="rounded-lg border border-border bg-muted/20 px-3 py-2 text-center"
                            >
                                <p className={cn("text-lg font-semibold tabular-nums leading-none", stat.className)}>
                                    {stat.value}
                                </p>
                                <p className="text-[11px] text-muted-foreground mt-1">{stat.label}</p>
                            </div>
                        ))}
                    </div>
                )}

                <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-1 border-t border-border">
                    <p className="text-xs text-muted-foreground flex-1">{hint}</p>
                    <div className="flex items-center gap-2 shrink-0">
                        <Button
                            variant={file && !summary ? "brand" : "outline"}
                            size="sm"
                            disabled={isBusy || !file}
                            onClick={() => send(true)}
                        >
                            {busy === "preview" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                            Preview
                        </Button>
                        <Button
                            variant={canApply ? "brand" : "outline"}
                            size="sm"
                            disabled={!canApply}
                            onClick={() => send(false)}
                        >
                            {busy === "apply"
                                ? <Loader2 className="h-4 w-4 animate-spin" />
                                : <Upload className="h-4 w-4" />}
                            Apply mapping
                        </Button>
                    </div>
                </div>
            </div>

            {preview && (
                <div className="border-t border-border px-4 sm:px-5 py-4">
                    <p className="text-xs font-medium text-muted-foreground mb-2">
                        Preview results — edit an HRZyra code, then preview again before applying.
                    </p>
                    <PreviewEditContext.Provider value={{ updateCode, busy }}>
                        <Reactable
                            pagename="biometric-mapping-preview"
                            data={rows}
                            setData={() => { }}
                            columns={PREVIEW_COLUMNS}
                            rowSize="sm"
                            fillHeight={false}
                            disableStateCache
                        />
                    </PreviewEditContext.Provider>
                </div>
            )}
        </div>
    );
};

export default MappingUploadPanel;
